import {
	RiCameraLine,
	RiCameraOffLine,
	RiChat4Line,
	RiChatOffLine,
	RiDeleteBinLine,
	RiDownload2Line,
	RiEyeLine,
	RiEyeOffLine,
	RiHammerLine,
	RiIndeterminateCircleLine,
	RiMoreFill
} from "@itandi/itandi-bb-ui";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import clsx from "clsx";
import { useRouter } from "next/router";
import type React from "react";
import type { ReactNode } from "react";
import { useState } from "react";
import type { DraggableProvided } from "react-beautiful-dnd";
import { Draggable } from "react-beautiful-dnd";
import { useSWRConfig } from "swr";
import { Dropdown } from "~/components/common/parts/dropdown/Dropdown";
import { DropdownList } from "~/components/common/parts/dropdown/DropdownList";
import { DropdownMenu } from "~/components/common/parts/dropdown/DropdownMenu";
import { ReportImageDeleteConfirmModal } from "~/components/common/reports/ReportImageDeleteConfirmModal";
import type { NskResponse } from "~/models/Api";
import type { DealReportImage } from "~/models/DealReportImage";
import {
	imageUploadedLabel,
	operableReportImage
} from "~/models/DealReportImage";
import { getReportImageSwrKey } from "~/utils/DealReportImageUtil";
import { ReportImageInput } from "./ReportImageInput";
import Styles from "./ReportImagesAreaSpRow.module.scss";

type Props = {
	companyType: "company" | "vendor";
	displaySeqNo: number | null;
	handleDestroy: (reportImage: DealReportImage) => Promise<NskResponse>;
	handleDownloadImage: (reportImage: DealReportImage) => void;
	handleUpdate: (submitValue: DealReportImage) => Promise<NskResponse>;
	handleUpdateSeq:
		| ((newOrderImages: DealReportImage[]) => Promise<void>)
		| null;
	index: number;
	onChangeSelectedImage: (reportImageId: number) => void;
	onPreviewOpen: (index: number) => void;
	reportImage: DealReportImage;
	selectedImageIds: readonly number[];
};

const renderView = (content: string | null | undefined): ReactNode => (
	<div className={Styles.ContentView}>
		{content === null || content === "" ? (
			<span className={Styles.YetInput}>未入力</span>
		) : (
			<span>{content}</span>
		)}
	</div>
);

export function ReportImagesAreaSpRow({
	reportImage,
	index,
	handleDownloadImage,
	handleUpdateSeq,
	handleUpdate,
	handleDestroy,
	companyType,
	displaySeqNo,
	onChangeSelectedImage,
	onPreviewOpen,
	selectedImageIds
}: Props) {
	const router = useRouter();
	const { mutate: globalMutate } = useSWRConfig();
	const [isOpenDelete, setIsOpenDelete] = useState(false);

	const handleMutateReportImages = async (): Promise<void> => {
		await globalMutate(getReportImageSwrKey(router.asPath, reportImage.dealId));
	};

	const isVendor = companyType === "vendor";
	const operableImage = operableReportImage(reportImage, companyType);
	const isShared = reportImage.shared;
	const isOutputPdfImage = reportImage.outputPdfImage;
	const isOutputPdfNote = reportImage.outputPdfNote;
	const uploadLabel = imageUploadedLabel(isVendor, reportImage);
	const submitPoint = async (submitValue: string): Promise<void> => {
		const newReportImage = { ...reportImage, point: submitValue };
		await handleUpdate(newReportImage);
		await handleMutateReportImages();
	};
	const submitNote = async (submitValue: string): Promise<void> => {
		const newReportImage = { ...reportImage, note: submitValue };
		await handleUpdate(newReportImage);
		await handleMutateReportImages();
	};
	const onClickImage = (e: React.MouseEvent): void => {
		e.preventDefault();
		onPreviewOpen(index);
	};
	const handleAcceptUpdateShared = async (
		submitValue: boolean
	): Promise<void> => {
		const newReportImage = { ...reportImage, shared: submitValue };
		await handleUpdate(newReportImage);
		await handleMutateReportImages();
	};
	const handleAcceptUpdateOutputPdf = async (
		submitKey: "outputPdfImage" | "outputPdfNote",
		submitValue: boolean
	): Promise<void> => {
		const newReportImage = { ...reportImage, [submitKey]: submitValue };
		await handleUpdate(newReportImage);
		await handleMutateReportImages();
	};
	const handleAcceptUpdateConstruction = async (
		submitValue: "unspecified" | "targeted"
	): Promise<void> => {
		await handleUpdate({ ...reportImage, construction: submitValue });
		await handleMutateReportImages();
	};
	return (
		<Draggable
			draggableId={String(reportImage.id)}
			index={index}
			isDragDisabled={handleUpdateSeq === null}
			key={reportImage.id}
		>
			{(provided: DraggableProvided): React.ReactElement<HTMLElement> => (
				<div
					className={clsx(
						reportImage.construction === "targeted" && Styles.Targeted
					)}
				>
					<div
						className={Styles.ReportImageRow}
						ref={provided.innerRef}
						style={provided.draggableProps.style}
						{...provided.draggableProps}
					>
						<div
							className={Styles.DragHandleWrapper}
							{...provided.dragHandleProps}
						>
							<img alt="drag handle" src="/assets/DragHandle.svg" />
						</div>
						<div className={Styles.ImageBlock}>
							<a href="" onClick={onClickImage}>
								<img
									alt="報告画像"
									className={Styles.Image}
									src={reportImage.thumbnailImageUrl ?? reportImage.imageUrl}
								/>
							</a>
							<div className={Styles.Plates}>
								{isVendor || isOutputPdfImage ? (
									<div className={Styles.Plate}>No.{displaySeqNo}</div>
								) : null}
								{uploadLabel ? (
									<div className={Styles.Plate}>{uploadLabel}</div>
								) : null}
							</div>
							{isVendor && !isShared ? (
								<p className={Styles.NotSharedPlate}>
									管理会社に共有されません
								</p>
							) : null}
							{!isVendor && !isOutputPdfImage && (
								<p className={Styles.NotOutPutPdf}>
									画像は報告書に出力されません
								</p>
							)}
							<p className={Styles.ImageCheckbox}>
								<InputCheckBox
									checked={selectedImageIds.includes(reportImage.id)}
									onChange={(): void => {
										onChangeSelectedImage(reportImage.id);
									}}
								/>
							</p>
							<div className={Styles.ButtonWrapper}>
								<Dropdown
									className={Styles.MenuDropdown}
									noBorder
									title={
										<span className={Styles.MenuButton}>
											<RiMoreFill className={Styles.MenuIcon} />
										</span>
									}
									useCaret={false}
									variant="link"
								>
									<DropdownList>
										<DropdownMenu
											onClick={(): void => {
												handleDownloadImage(reportImage);
											}}
										>
											<RiDownload2Line className={Styles.DropdownMenuIcon} />
											画像をダウンロード
										</DropdownMenu>
										{operableImage ? (
											<DropdownMenu
												onClick={(): void => {
													handleAcceptUpdateConstruction(
														reportImage.construction === "targeted"
															? "unspecified"
															: "targeted"
													).then();
												}}
											>
												{reportImage.construction === "targeted" ? (
													<>
														<RiIndeterminateCircleLine
															className={Styles.DropdownMenuIcon}
														/>
														施工推奨から外す
													</>
												) : (
													<>
														<RiHammerLine className={Styles.DropdownMenuIcon} />
														施工推奨にする
													</>
												)}
											</DropdownMenu>
										) : null}
										{isVendor && operableImage ? (
											<DropdownMenu
												onClick={(): void => {
													handleAcceptUpdateShared(!reportImage.shared);
												}}
											>
												{isShared ? (
													<>
														<RiEyeOffLine className={Styles.DropdownMenuIcon} />
														画像を共有しない
													</>
												) : (
													<>
														<RiEyeLine className={Styles.DropdownMenuIcon} />
														画像を共有する
													</>
												)}
											</DropdownMenu>
										) : null}
										{!isVendor && (
											<DropdownMenu
												onClick={(): void => {
													handleAcceptUpdateOutputPdf(
														"outputPdfImage",
														!reportImage.outputPdfImage
													).then();
												}}
											>
												{isOutputPdfImage ? (
													<>
														<RiCameraOffLine
															className={Styles.DropdownMenuIcon}
														/>
														画像を報告書に含めない
													</>
												) : (
													<>
														<RiCameraLine className={Styles.DropdownMenuIcon} />
														画像を報告書に含める
													</>
												)}
											</DropdownMenu>
										)}
										{!isVendor && (
											<DropdownMenu
												className={!isOutputPdfImage ? Styles.disabled : ""}
												onClick={(): void => {
													handleAcceptUpdateOutputPdf(
														"outputPdfNote",
														!reportImage.outputPdfNote
													).then();
												}}
											>
												{isOutputPdfNote ? (
													<>
														<RiChatOffLine
															className={Styles.DropdownMenuIcon}
														/>
														備考を報告書に含めない
													</>
												) : (
													<>
														<RiChat4Line className={Styles.DropdownMenuIcon} />
														備考を報告書に含める
													</>
												)}
											</DropdownMenu>
										)}
										{operableImage ? (
											<DropdownMenu
												onClick={(): void => {
													setIsOpenDelete(true);
												}}
											>
												<span className={Styles.DeleteLink}>
													<RiDeleteBinLine
														className={Styles.DropdownMenuIcon}
													/>
													画像を削除
												</span>
											</DropdownMenu>
										) : null}
									</DropdownList>
								</Dropdown>
							</div>
							{isOpenDelete ? (
								<ReportImageDeleteConfirmModal
									onClose={() => {
										setIsOpenDelete(false);
									}}
									onDelete={handleDestroy}
									reportImage={reportImage}
								/>
							) : null}
						</div>
						<div className={Styles.RemarksBlock}>
							{/* <div className={Styles.No}>No.{index + 1}</div> */}
							{reportImage.construction === "targeted" && (
								<div className={Styles.RemarkRow}>
									<div className={Styles.ConstructionState}>施工推奨箇所</div>
								</div>
							)}
							<div className={Styles.RemarkRow}>
								<div className={Styles.Label}>撮影場所</div>
								<div className={Styles.Content}>
									{operableImage ? (
										<ReportImageInput
											handleSubmit={submitPoint}
											inputType="point"
											value={reportImage.point ?? ""}
											viewComponent={renderView(reportImage.point)}
										/>
									) : (
										<>{renderView(reportImage.point)}</>
									)}
								</div>
							</div>
							<div className={Styles.RemarkRow}>
								<div className={Styles.Label}>備考</div>
								<div className={Styles.Content}>
									{operableImage ? (
										<ReportImageInput
											handleSubmit={submitNote}
											inputType="note"
											value={reportImage.note ?? ""}
											viewComponent={renderView(reportImage.note)}
										/>
									) : (
										<>{renderView(reportImage.note)}</>
									)}
									{!isVendor && isOutputPdfImage && !isOutputPdfNote ? (
										<p className={Styles.NotOutPutPdfNote}>
											備考は報告書に出力されません
										</p>
									) : null}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</Draggable>
	);
}
